import {Alert, Button, Segment} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {SaveSaleVehicleFinancingBody, VehicleFinancingType} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {FinancingCalculator} from './FinancingCalculator';
import {FinancingMatrix} from './FinancingMatrix';
import {FinancingCellCoordinates} from './types/FinancingCellCoordinates';
import {FinancingCellRates} from './types/FinancingCellRates';
import {FinancingDisplayMode} from './types/FinancingDisplayMode';
import {FinancingTemplateDraft} from './types/FinancingTemplateDraft';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {applyRatesToFinancingCells} from './utils/financingCells/applyRatesToFinancingCells';
import {ensureActiveFinancingCell} from './utils/financingCells/ensureActiveFinancingCell';
import {getFinancingCellKey} from './utils/financingCells/getFinancingCellKey';
import {getPaymentStep} from './utils/financingCells/getPaymentStep';
import {removeFinancingCell} from './utils/financingCells/removeFinancingCell';
import {getFinancingTypeLabel} from './utils/getFinancingTypeLabel';

interface FinancingRateMatrixSectionProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  type: VehicleFinancingType;
  template: FinancingTemplateDraft | undefined;
  displayMode: FinancingDisplayMode;
  balloonPayments: number[];
  activeBalloon: number;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  isTemplatePending: boolean;
  isReadOnly: boolean;
  hasActiveVariantError: boolean;
  selectionEpoch: number;
  onDisplayModeChange: (displayMode: FinancingDisplayMode) => void;
  onSelectedBalloonChange: (balloonPayment: number | null) => void;
  onEditCell: (coordinates: FinancingCellCoordinates) => void;
  onOpenConfiguration?: () => void;
  onChange: (vehicleFinancingBody: SaveSaleVehicleFinancingBody) => void;
}

export function FinancingRateMatrixSection(props: FinancingRateMatrixSectionProps) {
  const hasBalloonBracketSelector =
    props.type === 'BALLOON' && props.displayMode === 'MATRIX' && props.balloonPayments.length > 0;
  const isTemplateMatrix =
    props.displayMode === 'MATRIX' && props.vehicleFinancingBody.mode === 'TEMPLATE';
  const hasNoTerms = props.vehicleFinancingBody.rateMatrix.availableTerms.length === 0;
  const hasNoRates = !hasNoTerms && props.vehicleFinancingBody.rateMatrix.cells.length === 0;

  const handleBulkApply = (selection: FinancingCellCoordinates[], rates: FinancingCellRates) => {
    const nextBody = {
      ...props.vehicleFinancingBody,
      rateMatrix: {
        ...props.vehicleFinancingBody.rateMatrix,
        cells: applyRatesToFinancingCells(
          props.vehicleFinancingBody.rateMatrix.cells,
          selection,
          rates
        ),
      },
    };

    props.onChange(
      props.vehicleFinancingBody.rateMatrix.cells.length === 0
        ? ensureActiveFinancingCell(nextBody)
        : nextBody
    );
  };

  const handleBulkDelete = (selection: FinancingCellCoordinates[]) => {
    const selectedKeys = new Set(selection.map(getFinancingCellKey));
    const cellsToRemove = props.vehicleFinancingBody.rateMatrix.cells.filter((cell) =>
      selectedKeys.has(getFinancingCellKey(cell))
    );

    props.onChange(cellsToRemove.reduce(removeFinancingCell, props.vehicleFinancingBody));
  };

  return (
    <VStack spacing={3}>
      <HStack justify="space-between" align="center" spacing={4} wrap>
        <HStack align="center" spacing={2} wrap>
          <Heading size={4}>{i18n.t('page.vehicle.financingAndLeasing.rateMatrix')}</Heading>
          <Show when={hasBalloonBracketSelector}>
            {props.balloonPayments.map((balloonPayment) => (
              <Button
                key={balloonPayment}
                size="small"
                title={`${balloonPayment} %`}
                variant={props.activeBalloon === balloonPayment ? 'primary' : 'secondary'}
                onClick={() => props.onSelectedBalloonChange(balloonPayment)}
              />
            ))}
          </Show>
        </HStack>
        <Box minWidth={80}>
          <Segment<FinancingDisplayMode>
            value={props.displayMode}
            onChange={props.onDisplayModeChange}
            options={[
              {
                value: 'MATRIX',
                label: i18n.t('page.vehicle.financingAndLeasing.matrix'),
                leftIcon: 'editor/table_chart',
                leftIconColor: props.displayMode === 'MATRIX' ? 'general.accent' : 'text.secondary',
              },
              {
                value: 'CALCULATOR',
                label: i18n.t('page.vehicle.financingAndLeasing.calculator'),
                leftIcon: 'editor/attach_money',
                leftIconColor:
                  props.displayMode === 'CALCULATOR' ? 'general.accent' : 'text.secondary',
              },
            ]}
          />
        </Box>
      </HStack>

      <Show when={isTemplateMatrix && (hasNoTerms || hasNoRates)}>
        <HStack spacing={2} align="center" wrap>
          <Text color="secondary">
            {i18n.t(
              hasNoTerms
                ? 'entity.vehicleFinancingTemplate.labels.selectTerms'
                : 'page.vehicle.financingAndLeasing.templateWithoutRates'
            )}
          </Text>
          <Show when={props.onOpenConfiguration}>
            <Button
              title={i18n.t('page.vehicle.financingAndLeasing.editConfiguration')}
              variant="link"
              leftIcon="action/settings"
              onClick={() => props.onOpenConfiguration?.()}
            />
          </Show>
        </HStack>
      </Show>
      <Show when={props.displayMode === 'MATRIX'}>
        <FinancingMatrix
          // Deliberately without the balloon bracket: the matrix scopes its own selection to
          // the bracket it renders, so a bracket switch no longer tears down every cell.
          key={`${props.type}-${props.selectionEpoch}`}
          vehicleFinancingBody={props.vehicleFinancingBody}
          type={props.type}
          balloonPayment={props.type === 'BALLOON' ? props.activeBalloon : null}
          vehiclePrice={props.vehiclePrice}
          currency={props.currency}
          pricing={props.pricing}
          templateCells={props.template?.rateMatrix.cells ?? []}
          isTemplatePending={props.isTemplatePending}
          isReadOnly={props.isReadOnly}
          onBulkApply={handleBulkApply}
          onBulkDelete={handleBulkDelete}
          onEditCell={props.onEditCell}
        />
      </Show>
      <Show when={props.displayMode === 'CALCULATOR'}>
        <FinancingCalculator
          vehicleFinancingBody={props.vehicleFinancingBody}
          type={props.type}
          balloonPayment={props.type === 'BALLOON' ? props.activeBalloon : null}
          balloonPayments={props.balloonPayments}
          vehiclePrice={props.vehiclePrice}
          currency={props.currency}
          pricing={props.pricing}
          downPaymentStep={getPaymentStep(props.template?.downPaymentStep ?? null)}
          onBalloonPaymentChange={props.onSelectedBalloonChange}
        />
      </Show>
      <Show when={props.hasActiveVariantError}>
        <Alert
          type="inline"
          variant="error"
          title={i18n.t('page.vehicle.financingAndLeasing.validation.activeVariantRequiredTitle', {
            product: getFinancingTypeLabel(props.type),
          })}
          message={i18n.t(
            'page.vehicle.financingAndLeasing.validation.activeVariantRequiredMessage',
            {action: i18n.t('page.vehicle.financingAndLeasing.setAsActiveVariant')}
          )}
        />
      </Show>
    </VStack>
  );
}
